import { Injectable, ErrorHandler, Inject, Optional } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { BugsnagErrorHandler } from './bugsnag-error-handler';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';

import * as sourcemappedStacktrace from 'sourcemapped-stacktrace';
import * as express from '../common/express-ssr';
import { environment } from '@tytapp/environment';
import { ApiAppConfig } from 'api';
import { LoggerService } from '@tytapp/common/logger.service';
import { Location } from '@angular/common';
import Bugsnag from '@bugsnag/js';

@Injectable()
export class AppErrorHandler implements ErrorHandler {
    constructor(
        @Inject(REQUEST) @Optional()
        private request : express.ExpressRequest,
        @Inject(RESPONSE) @Optional()
        private response : express.ExpressResponse,
        private logger : LoggerService,
        private location : Location
    ) {
        this.bugsnag = new BugsnagErrorHandler();
    }

    private bugsnag : ErrorHandler;

    public appStatus : ApiAppConfig;
    panicked : boolean = false;
    private _networkError : Subject<any> = new Subject<any>();
    
    public get networkError(): Observable<any> {
        return this._networkError;
    }

    /**
     * Handle an uncaught error.
     * @param error 
     */
    handleError(error) {
        try {
            this.bugsnag.handleError(error);
        } catch (e) {
            console.error(`Bugsnag error handler threw an error`);
        }

        let clientSide = typeof window !== 'undefined';

        while (error.rejection) {
            if (clientSide) {
                console.groupCollapsed(`Stripping promise rejection layer from original error data:`);
                console.dir(error);
                console.groupEnd();
            }
            error = error.rejection;
        }

        if (error.message && error.message == 'The YouTube player is not attached to the DOM.')
            return;
        
        if (error.errorType == 'network-error') {
            this._networkError.next(error);
            return;
        }

        if (environment.testing) {
            if (error.stack)
                this.logger.error(`Error: ${error.message} -- Stack: ${error.stack}`);
            else
                this.logger.error(`Error: ${error.message}`);

            sourcemappedStacktrace.mapStackTrace(error.stack, mappedStack => {
                this.logger.error(`Sourcemapped stack trace for error '${error.message}': ${mappedStack.join("\n")}`);
            })
            return;
        }

        // If we have previously errored, do not error further.
        // This is only really useful in dev mode, but at least for production 
        // we won't go and reload the fancy BG url if that is turned on.
        
        if (this.panicked)
            return;

        let side : string = (typeof window === 'undefined') ? 'server-side' : 'client-side';
    
        if (error.message && error.message.includes('#_=_')) {
            return;
        }

        // Spot.im bug circa August 2021
        if (error.message && ["Cannot read property 'src' of undefined", "Cannot read properties of undefined (reading 'src')"].includes(error.message))
            return;

        if (typeof window !== 'undefined' && error.message && /Loading chunk \d+ failed./.test(error.message)) {
            // Looks like our application version is no longer available. 
            // This should be a rare circumstance. Report the event to the server and attempt to gracefully recover.

            this.logger.error(`${error.message}, details: ${error}`);

            Bugsnag.notify(new Error(`The application version on-server was replaced and we could not load a chunk. (${side})`));
            
            // Attempt to reload the page (only if we can guarantee that it will happen exactly once)
            // Hopefully the user will get the new app version with a minor inconvenience

            if (window.localStorage) {
                let allowReload = true;

                if (window.location.pathname === '/cast/receiver')
                    allowReload = false;
                
                if (window.localStorage['appVersionFailureDate']) {
                    let lastDate = parseInt(window.localStorage['appVersionFailureDate']);

                    if (new Date().getTime() - 1000*60 < lastDate) {
                        allowReload = false;
                    }
                }

                if (allowReload)
                    window.location.reload();
            }
        }

        this.panicked = true;
        let shownError : Error = error;

        if (error.panic)
            shownError = error.panicError;

        if (shownError.stack) {
            this.logger.error(`Panic: ${shownError.stack}`);
        } else {
            this.logger.error(`Panic: ${JSON.stringify(shownError)}`);
        }

        if (environment.showPanics) {
            this.showPanic(shownError);

            // If the user navigates, force a page refresh. This prevents getting "stuck" in the 
            // panic screen.
    
            if (typeof window !== 'undefined' && window.location.pathname !== '/cast/receiver') {
                window.addEventListener('popstate', () => {
                    console.log(`Reloading on back-navigation due to panic`);
                    window.location.reload();
                });
            }

            return;
        }
    }

    /**
     * Show the user the panic page
     */
    private showPanic(shownError) {

        let envName = environment.name || 'development';
        let enableProductionPanic = true;

        if (!shownError)
            shownError = new Error("No error details are available");

        if (!enableProductionPanic || envName != 'production') {
            if (typeof document !== 'undefined') {
                let style = document.createElement('style');
                style.innerText = `
                    #p-error {
                        overflow-y: hidden; 
                        display: none;
                        position: fixed;
                        top: 0; left: 0;
                        bottom: 0; right: 0;
                        background: #ccc;
                        color: black;
                        z-index: 99999;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        background: #333;
                    }
                
                    #p-error .p-logo {
                        width: 150px;
                        margin: 0 auto;
                        display: block;
                    }
                    #p-error .p-logo .st0 {
                        fill: white;
                    }
                    #p-error > div {
                        max-height: 100%; 
                        overflow-y: auto; 
                        margin: 1em;
                        width: 40em;
                        max-width: 100%;
                        border: 1px solid #767676;
                        border-radius: 5px;
                        background: #555;
                        padding: 1em;
                        margin: 1em;
                        color: white;
                    }
                    #p-error.visible {
                        display: flex !important;
                        justify-content: center; 
                        align-items: center;
                    }
                `;
                
                let pError = document.createElement('div');

                pError.innerHTML = `
                    <div id="p-error">
                    <div>
                        <h1>
                        <svg class="p-logo" class="cast-splash-logo" version="1.1" x="0px" y="0px"
                            viewBox="0 0 279.1 198.2" style="enable-background:new 0 0 279.1 198.2;" xml:space="preserve">
                            
                            <path d="M265,0h-251C6.3,0,0,6.3,0,14.1v126.2c0,6,3.8,11.3,9.4,13.3L134,197.4c3,1,6.2,1.1,9.2,0.1l126.4-42.6
                            c5.7-1.9,9.6-7.3,9.6-13.3V14.1C279.1,6.3,272.8,0,265,0z"/>
                            <g>
                            <path class="st0" d="M94.6,49.1l-6.5-19.6c-0.4-1.2-1.5-2-2.7-2H24.6c-1.8,0-3.2,1.7-2.8,3.5l4.4,19.6c0.3,1.3,1.5,2.3,2.8,2.3
                                h17.3c1.6,0,2.9,1.3,2.9,2.9v84c0,1.3,0.9,2.5,2.3,2.8l21.1,4.7c1.8,0.4,3.5-1,3.5-2.8V55.8c0-1.6,1.3-2.9,2.9-2.9h12.9
                                C93.8,52.9,95.2,50.9,94.6,49.1z"/>
                            <path class="st0" d="M184.5,49.1l6.5-19.6c0.4-1.2,1.5-2,2.7-2h60.7c1.8,0,3.2,1.7,2.8,3.5l-4.4,19.6c-0.3,1.3-1.5,2.3-2.8,2.3
                                h-17.3c-1.6,0-2.9,1.3-2.9,2.9v84c0,1.3-0.9,2.5-2.3,2.8l-21.1,4.7c-1.8,0.4-3.5-1-3.5-2.8V55.8c0-1.6-1.3-2.9-2.9-2.9h-12.9
                                C185.2,52.9,183.9,50.9,184.5,49.1z"/>
                            <path class="st0" d="M154.2,29.5l-14.7,45.4l-14.7-45.4c-0.4-1.2-1.5-2-2.7-2h-21.7c-2,0-3.4,2-2.7,3.8l26.4,75.3
                                c0.1,0.3,0.2,0.6,0.2,0.9l0.3,51.8c0,1.3,0.9,2.5,2.2,2.8l12.2,3c0.5,0.1,0.9,0.1,1.4,0l12.2-3c1.3-0.3,2.2-1.5,2.2-2.8l0.3-51.8
                                c0-0.3,0.1-0.6,0.2-0.9l26.4-75.3c0.7-1.9-0.7-3.8-2.7-3.8h-21.7C155.6,27.5,154.5,28.3,154.2,29.5z"/>
                            </g>
                        </svg>
                        </h1>
                        <div style="display: flex; flex-direction: row; align-items: center; margin: 1em 0;">
                        <hr style="flex: 1;" />
                        <div style="margin: 0 1em; text-transform: uppercase; color: #ccc; letter-spacing: 2px;">Details</div>
                        <hr style="flex: 1;" />
                        </div>
                        <h2 id="dev-panic-message"></h2>
                        <pre id="dev-panic-backtrace"></pre>
                    </div>
                    </div>

                
                `;
                document.body.appendChild(pError);
                document.body.appendChild(style);

                document.getElementById('p-error').className = 'visible';

                if (shownError.message)
                    document.getElementById('dev-panic-message').innerText = shownError.message;
                else
                    document.getElementById('dev-panic-message').innerHTML = `<pre>${JSON.stringify(shownError, undefined, 2)}</pre>`;
                
                if (shownError.stack)
                    document.getElementById('dev-panic-backtrace').innerHTML = (shownError.stack || "").replace(/\n/g, "<br/>\n");
                else
                    document.getElementById('dev-panic-backtrace').innerText = `No stack trace available.`;

                if (shownError.stack) {
                    sourcemappedStacktrace.mapStackTrace(shownError.stack, mappedStack => {
                        document.getElementById('dev-panic-backtrace').innerHTML = mappedStack.join("<br/>\n");
                    });
                }
            } 
        } else {
            if (typeof document !== 'undefined') {
                document.getElementById('panic').className = 'visible';
            }
        }

        // Generic adjustments to document

        if (typeof document !== 'undefined') {

            document.documentElement.style.overflowY = 'hidden';
            document.body.style.overflowY = 'hidden';

            // Inject the URL of the YouTube video embed for the panic BG background for production
            let iframe = <HTMLIFrameElement>document.getElementById('panic-bg-video');
            if (iframe)
                iframe.src = 'https://www.youtube.com/embed/3aF_FABP4PU?autoplay=1&loop=1&modestbranding=1&playsinline=1&controls=0&showinfo=0&rel=0';
        }
    }
}